import * as Yup from 'yup';
import Banner from '@cpg-its/ads.containers.banner';
import React, { useContext, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { Grid, Hidden } from '@material-ui/core';
import { ChildTypes, Dependent, DependentTypes } from './dependents-model';
import { RelationshipsProps } from '../../models/account-info-epi-props';
import { getRequiredText } from '../../../../global/helpers/transformer';
import { PersonInfoValidationSchema } from '../../../../global/validation-schemas/account-information-form-validation';
import { DependentFormValues } from './dependent-form-models';
import { FormWrapper } from '../../../../global/components/form-wrapper';
import { LabelSettingProps } from '../../../../global/models/epi-model';
import { getFormattedDate, isChildOlderThanPerson } from '../../../../global/helpers/date-helpers';
import { PersonInfoMailingAddressValidationSchema } from '../../../../global/validation-schemas/address-form-validation';
import { useApiResponseError } from '../../../../global/hooks/useApiError';
import { DependentFormFields } from './dependent-form-fields';
import { getInitialEmails, getInitialMailingAddress, getInitialPersonInfo, getInitialPhones } from '../../helpers/form-init-helpers';
import { DependentConfirmation } from './dependent-confirmation';
import { DemographicAppKeys, DemographicsContext } from '../../../../global/models/demographics-model';
import { PersonSearchResults } from '../../../arc/features/person-search/person-search-results';
import { PerformPersonSearchContext, PersonSearchContext, PersonSearchState, SearchPerson } from '../../../arc/features/person-search/person-search-model';
import { TcsmStates } from '../../../../root/tcsm-model';
import { apiErrorClear } from '../../../../global/ducks/base-api';
import { shouldContinueButtonBeDisabled, transformSearchPersonToDependentFormValues } from '../../../arc/features/person-search/oscrr-helpers';
import { PersonSearchToastCombo } from '../../../arc/features/person-search/person-search-toast-combo';
import { FormTypes, matchedPersonField } from '../../models/form-models';
import { PageHeader } from '../../../../global/components/layout/page-header';
import { FormBaseModel } from '../../../../global/models/page-model';
import { SideBarContent } from '../../../../global/components/layout/sidebar-content';
import { getCurrentFormattedDateNY } from '../../../../global/helpers/form-data-helper';
import { Person } from '../../../../global/models/person-model';
import { noSelectPersonId } from '../../../arc/models/arc-epi-props';
import { ContentSpacer } from '../../../../global/components/layout/generic-content-spacer';
import { FormGroup } from '../../../../global/components/layout/form-group';
import { FormField } from '../../../../global/components/form-element/form-field';
import { getChildType } from '../../../annual-enrollment/helpers/annual-enrollment-helpers';
import { GpsType } from '../../../../global/components/layout/layout-models';
import { getGpsType } from '../../../../global/helpers/gps-helper';
import { GuidedProcessSupportBlock } from '../../../../global/components/guided-process-support-block';


interface DependentFormProps extends FormBaseModel {
	onSubmitHandler: (values: DependentFormValues, savedValues: DependentFormValues, formikHelpers: FormikHelpers<DependentFormValues>, selectedPerson: SearchPerson) => any,
	dependent: Dependent,
	dependentType: DependentTypes;
	relationshipsProps: RelationshipsProps;
	labelSetting: LabelSettingProps,
	isAdd: boolean;
	previousRoute: string;
	showConfirmation: boolean;
	setShowConfirmation: (value: boolean) => void;
	currentPerson: Person;
	childType: string;
	skipDocUpload: boolean;
}

export function DependentForm(props: DependentFormProps) {
	const {
		dependent,
		dependentType,
		relationshipsProps,
		labelSetting,
		onSubmitHandler,
		isAdd,
		previousRoute,
		showConfirmation,
		setShowConfirmation,
		currentPerson,
		childType,
		skipDocUpload,
	} = props;

    const groupName = matchedPersonField;
    const app = useContext(DemographicsContext);
    const personSearchKey = useContext(PersonSearchContext);
    const personSearchState = useSelector<TcsmStates, PersonSearchState>(state => state.personSearchState[personSearchKey]);
    const history = useHistory();
    const searchResultsRef = useRef<HTMLDivElement>();
    const dispatch = useDispatch();
    const apiErrors = useApiResponseError();
    const dependentIsClergy = !isAdd && (dependent?.person?.cleric_in_book === 'Y');
    const hasSearchResults: boolean = personSearchState?.searchResults?.length > 0;

    const isMyCpg = (app === DemographicAppKeys.myCPG);

    const hasAttestations = ((app === DemographicAppKeys.arc) && showConfirmation) ||
        ((app === DemographicAppKeys.myCPG) && !showConfirmation);

    const dependentsAttestationLabel1 = isMyCpg ? relationshipsProps.dependentsAttestationLabel1 : relationshipsProps.dependentsMapAttestationLabel1;
    const dependentsAttestationLabel2 = isMyCpg ? relationshipsProps.dependentsAttestationLabel2 : relationshipsProps.dependentsMapAttestationLabel2;


    const gpsType = isAdd ? GpsType.ADD_DEPENDENT : GpsType.EDIT_DEPENDENT;
    const gpsItem = getGpsType(gpsType, app);
    // no form validation on confirmation page
    const validationSchema = Yup.lazy(({
        date_of_birth,
        matched_person,
        business_phone_formated,
        business_phone_country_code,
        mobile_phone_formated,
        mobile_phone_country_code,
        home_phone_formated,
        home_phone_country_code }) => {

        let schema = Yup.object().shape({});

        if (hasSearchResults && !matched_person) {
            schema = schema.shape({
                matched_person: Yup.string().nullable().required(labelSetting.selectMatchPersonRequired),
            });
        } else if (!hasSearchResults || (hasSearchResults && matched_person === noSelectPersonId)) {

            schema = showConfirmation ? Yup.object().shape({}) : Yup.object().shape({
                start_date: Yup.date().nullable().max(new Date(Date.now()), labelSetting.dateFutureError || 'Date cannot be in the future').when('child_type', {
                    is: ChildTypes.step,
                    then: Yup.date().nullable().required(getRequiredText(labelSetting.requiredFormat, relationshipsProps.relationshipStartDateLabel)).max(new Date(Date.now()), labelSetting.dateFutureError || 'Date cannot be in the future'),
                })
                .when('child_type', {
                    is: ChildTypes.adopted,
                    then: Yup.date().nullable().required(getRequiredText(labelSetting.requiredFormat, relationshipsProps.adoptionStartDateLabel)).max(new Date(Date.now()), labelSetting.dateFutureError || 'Date cannot be in the future'),
                }),
                child_type: Yup.string().required(getRequiredText(labelSetting.requiredFormat, relationshipsProps.childTypeLabel)),
                disabled_child_indicator: Yup.string().required(getRequiredText(labelSetting.requiredFormat, relationshipsProps.disabledChildQuestionLabel)),
                ...PersonInfoValidationSchema(
                    labelSetting,
                    date_of_birth,
                    {
                        business_phone_formated,
                        business_phone_country_code,
                        mobile_phone_formated,
                        mobile_phone_country_code,
                        home_phone_formated,
                        home_phone_country_code,
                    },
                    false, true),
                ...PersonInfoMailingAddressValidationSchema(labelSetting),
            });

            if (dependentIsClergy) {
                schema = schema.shape({
                    designation_salutation: Yup.string().required(getRequiredText(labelSetting.requiredFormat, labelSetting.salutationLabel)),
                });
            }

            if (hasAttestations) {
                const dependentsAttestationRequiredMessage1 = isMyCpg ? relationshipsProps.dependentsAttestationRequiredMessage1 : relationshipsProps.dependentsMapAttestationRequiredMessage1;
                const dependentsAttestationRequiredMessage2 = isMyCpg ? relationshipsProps.dependentsAttestationRequiredMessage2 : relationshipsProps.dependentsMapAttestationRequiredMessage2;

                schema = schema.shape({
                    attestation1: Yup.boolean().required(getRequiredText(labelSetting.requiredFormat, dependentsAttestationRequiredMessage1))
                        .oneOf([true], getRequiredText(labelSetting.requiredFormat, dependentsAttestationRequiredMessage1)),
                    attestation2: Yup.boolean().required(getRequiredText(labelSetting.requiredFormat, dependentsAttestationRequiredMessage2))
                        .oneOf([true], getRequiredText(labelSetting.requiredFormat, dependentsAttestationRequiredMessage2)),
                });
            }
        }

        return schema;
    });

    const initialValues: DependentFormValues = {
        start_date: (getChildType(dependent) === ChildTypes.adopted || getChildType(dependent) === ChildTypes.step) ? dependent?.start_date : null,

		dependent_type: dependentType,
        child_type: getChildType(dependent) || childType || ChildTypes.legal,
		disabled_child_indicator: dependent?.person?.disabled_child_indicator || '',

        ...getInitialPersonInfo(dependent?.person),
        ...getInitialMailingAddress(dependent?.person),
        ...getInitialPhones(dependent?.person),
        ...getInitialEmails(dependent?.person),
        matched_person: null,
        attestation1: false,
        attestation2: false,
    };

    const [selectedPerson, setSelectedPerson] = useState(null);
    const [cachedFormValues, setCachedFormValues] = useState(initialValues);

    // on cancel, go back if on confirmation page. Also set values back to form if person was selected
    const onCancel = (formProps: FormikProps<DependentFormValues>) => {
        if (showConfirmation) {
            formProps.setValues(cachedFormValues);
            formProps.setFieldValue(groupName, null);
            formProps.setTouched({});
            setShowConfirmation(false);
            setSelectedPerson(null);
        } else {
            history.push(previousRoute);
        }
    };

    const isAdminAdd = () => {
        return isAdd && app === DemographicAppKeys.arc;
    };

    const addressExpired = getFormattedDate(dependent?.person?.mailing_address_expiration_date) < getCurrentFormattedDateNY();
    const [addressExpiredError, setAddressExpiredError] = useState(addressExpired);

    const renderFormContent = (formProps: FormikProps<DependentFormValues>) => {
        const adminAdd = isAdminAdd();

        if (adminAdd && showConfirmation) {
            return (
                <DependentConfirmation
                    labelSetting={labelSetting}
                    relationshipsProps={relationshipsProps}
                    formProps={formProps}
                    selectedPerson={!!selectedPerson}
                />
            );
        }

		return (
			<PerformPersonSearchContext.Provider value={adminAdd}>
				<DependentFormFields
					formProps={formProps}
					relationshipsProps={relationshipsProps}
					labelSetting={labelSetting}
					isAdd={isAdd}
					addressExpired={addressExpired}
					addressExpiredError={addressExpiredError}
					setAddressExpiredError={setAddressExpiredError}
					currentPerson={dependent?.person}
					dependentIsClergy={dependentIsClergy}
					dobWarningMessage={
						isChildOlderThanPerson(formProps?.values?.dependent_type, formProps?.values?.date_of_birth, currentPerson.date_of_birth, currentPerson.masked_date_of_birth) &&
						<ContentSpacer>
							<Banner
								banner={{
									type: 'warning',
									label: props?.relationshipsProps?.childIsOlderWarningMessage,
								}}
							/>

						</ContentSpacer>
					}
					skipDocUpload={skipDocUpload}
				/>
			</PerformPersonSearchContext.Provider>

        );
    };

    const getButtonText = () => {
        if (isAdminAdd()) {
            return showConfirmation ? [labelSetting.submitText, labelSetting.goBackText] : [labelSetting.continueText, labelSetting.cancelGoBackText];
        }
        return [labelSetting.submitText, labelSetting.cancelGoBackText];
    };

    const onNavigateToTop = () => {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    };

    const onPersonSelect = (person: SearchPerson, formProps: FormikProps<DependentFormValues>, values: DependentFormValues) => {
        dispatch(apiErrorClear());
        setSelectedPerson(person);
        setCachedFormValues(values);
        formProps.setValues(transformSearchPersonToDependentFormValues(person, values.dependent_type, values.child_type, values.start_date));
        formProps.setTouched({});
        setShowConfirmation(true);
        onNavigateToTop();
    };

    function onInternalSubmit(values: DependentFormValues, savedValues: DependentFormValues, formikHelpers: FormikHelpers<DependentFormValues>, selectedPersonItem: SearchPerson) {

        const formProps: FormikProps<DependentFormValues> = formikHelpers as FormikProps<DependentFormValues>;
        if (values) {
            const selectedPersonId = values[groupName];

            if (selectedPersonId &&
                selectedPersonId !== noSelectPersonId &&
                selectedPersonId !== selectedPerson?.guid &&
                personSearchState?.searchResults?.length > 0) {

                const selectedPersonGrid = personSearchState?.searchResults?.find(employee => employee.guid === selectedPersonId);

                if (selectedPersonGrid) {
                    onPersonSelect(selectedPersonGrid, formProps, values);
                    return;
                }
            }
        }

        onSubmitHandler(values, savedValues, formikHelpers, selectedPersonItem);
    }

    function renderAttestations(formProps: FormikProps<DependentFormValues>) {
        if (!hasAttestations || !formProps?.values?.child_type) return null;        

        return (
            <FormGroup>
                { }
                <div data-formdata='attestation1'>
                    <FormField
                        name='attestation1'
                        label={dependentsAttestationLabel1}
                        component='checkBox'
                        touched={formProps.touched}
                        errors={formProps.errors}
                        value={formProps?.values?.attestation1 ?? false}
                        checked={formProps?.values?.attestation1 ?? false}
                        required
                    />
                </div>
                <div data-formdata='attestation2'>
                    <FormField
                        data-formdata='attestation2'
                        name='attestation2'
                        label={dependentsAttestationLabel2}
                        component='checkBox'
                        touched={formProps.touched}
                        errors={formProps.errors}
                        value={formProps?.values?.attestation2 ?? false}
                        checked={formProps?.values?.attestation2 ?? false}
                        required
                    />
                </div>
            </FormGroup>
        );
    }

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={(values, formikHelpers) => onInternalSubmit(values, initialValues, formikHelpers, selectedPerson)}
            validationSchema={validationSchema}
            enableReinitialize
            initialErrors={apiErrors}
        >
            {(formProps: FormikProps<DependentFormValues>) => (
                <>
                    <Form>
                        <Grid>
                            <FormWrapper
                                formikProps={formProps}
                                submitText={getButtonText()[0]}
                                cancelText={getButtonText()[1]}
                                onCancel={() => onCancel(formProps)}
                                submitDisabled={addressExpiredError || shouldContinueButtonBeDisabled(formProps.values.matched_person, personSearchState?.searchResults)}
                                personSearchToast={
                                    <PersonSearchToastCombo
                                        searchResultsRef={searchResultsRef}
                                        renderResultsMessaging={isAdminAdd() && !showConfirmation && personSearchState.searchResults?.length > 0}
                                    />
                                }
                                formType={isAdd ? FormTypes.POST_DEPENDENT : FormTypes.PATCH_DEPENDENT}
                                defaultShowDocUpload={isAdd}
                                hideDocUpload={(isAdminAdd() && !showConfirmation) || !formProps?.values?.child_type}
                                additionalContentAboveButtons={renderAttestations(formProps)}
                                personSearchResults={
                                    isAdminAdd() && !showConfirmation &&
                                    <PersonSearchResults
                                        groupName={groupName}
                                        results={personSearchState?.searchResults}
                                        backToTopAction={onNavigateToTop}
                                        resultsRef={searchResultsRef} />
                                }
                                formKeys={Object.keys(initialValues)}>
                                <PageHeader
                                    headerText={props.formHeader}
                                    description={props.formDescription}
                                />
                                <Hidden mdUp>
                                    <SideBarContent>
                                        <GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
                                    </SideBarContent>
                                </Hidden>
                                {renderFormContent(formProps)}
                            </FormWrapper>
                        </Grid>
                    </Form>
                </>
            )}
        </Formik>
    );
}
